/**
 * Error contract shared by every surface.
 *
 * The patient client is operated by an untrained caregiver under time pressure
 * (ADR-0007), so an error code is only useful if it maps to something a person
 * can actually do. Every code below carries an operator-facing message; the
 * client picks the copy, the server never invents prose.
 */

export const ERROR_CODES = [
  /** Link token does not resolve to any session. Mistyped or fabricated. */
  'link_not_found',
  /** Session exists but has already ended. */
  'session_ended',
  /** Session exists but its link validity window has passed. */
  'link_expired',
  /** Session exists but the physio has not opened it yet. */
  'session_not_live',
  /** Request body or query failed schema validation. */
  'invalid_request',
  /** Referenced resource (artifact, record) does not exist. */
  'not_found',
  /** Recording consent is not on record for this session (C8/A3 gate). */
  'consent_required',
  /**
   * Presign refused: this artifact already has confirmed bytes (ADR-0014
   * write-once). The device must settle the task, never retry — the stored
   * recording wins.
   */
  'artifact_already_uploaded',
  /**
   * Presign refused: this artifact's stored object was PURGED at the end of
   * its retention (PRD H4). Purge is terminal — there is no restore, that is
   * what purge means (increment-9 plan §4) — so minting another PUT here
   * would let a device put back media the platform is on record as having
   * destroyed. 410, not 409: the resource existed and is intentionally gone.
   */
  'artifact_purged',
  /**
   * Export refused: this episode has no stored study code, so the projection
   * has no identity to put in the `patient` block — and the study code IS
   * that identity (increment-9 plan §0.1 call 3). Minting one on the fly is
   * exactly what call 3 ruled out: a code gets quoted in papers and IEC
   * submissions, so it is assigned once at enrolment and never derived.
   * 409, not 500: a knowable state of the record, not a server fault.
   */
  'export_no_study_code',
  /**
   * Export refused: the episode's pinned protocol template is unknown to this
   * build, so there is nothing to project the record AGAINST — schedule,
   * targets and flag definitions all come from the template (H3). Refusing
   * beats exporting a record measured against a guessed protocol.
   */
  'export_unknown_protocol',
  /**
   * Export refused: the episode has a signed session whose disposition is
   * `incomplete-technical` (SC6, ADR-0032). The bound dataset's status
   * vocabulary is fixed at `completed` / `completed-early-stop` (C9) — no
   * ratified third value exists yet for a crash-shortened day (OQ-1 names
   * the disposition, OQ-4 what a sign-off on one even requires), so this
   * refuses the WHOLE export rather than guess at a clinical claim. Once
   * ruled, this becomes a mapping, not a refusal — one line in studyExport.ts
   * and this code retires.
   */
  'export_incomplete_technical_unresolved',
  /**
   * Confirmation refused: the record is not ready to seal — session not yet
   * ended, or no protocol pinned (D3: the planned list is data, never
   * inferred).
   */
  'record_not_confirmable',
  /** Confirmation refused: a sign-off already exists — the record is sealed. */
  'record_already_confirmed',
  /**
   * Confirmation refused: a tap this seal cites or reconciles was superseded
   * after the form's prefill (a live undo synced late — ADR-0010 extension
   * ¶4). The form re-reads prefill and re-baselines; nothing was written.
   */
  'tap_superseded',
  /**
   * Assessment refused: the named day is not an assessment day of the
   * episode's pinned protocol template (E1 — the schedule is the template's,
   * never the client's).
   */
  'not_assessment_day',
  /**
   * Assessment refused: this episode × timepoint already has a root record.
   * A correction supersedes the existing record; it never double-mints.
   */
  'assessment_already_filed',
  /**
   * Observation refused: this session already has a root panel. A correction
   * supersedes it; it never double-mints (E4).
   */
  'observation_already_recorded',
  /**
   * Confirmation refused: no observation panel is recorded for this session
   * (E4, §0.1 value-or-explicit-skip). A NEW seal requires the complete
   * six-field panel — every field a number or an asserted "not assessed
   * today" — recorded first. Legacy sealed records are untouched: this gate
   * runs only where no sign-off exists yet.
   */
  'observation_missing',
  /**
   * Flag raise refused: this episode already has an OPEN flag with that code
   * (F1 idempotence — one open per episode × code). An auto rule that keeps
   * tripping does not stack, and neither does a hand.
   */
  'flag_already_open',
  /**
   * Flag resolution refused: this flag is already closed. A flag closes once;
   * recurrence is a NEW raise, never a second resolution (F2, append-only).
   */
  'flag_already_resolved',
  /**
   * Authenticated, but not the owner (TM-01, ADR-0017): the record belongs to
   * a different clinician. V1 ownership is the creating clinician; care-team
   * membership grants nothing. Deliberately distinct from not_found — ids are
   * unguessable UUIDs, and an honest refusal beats a fake absence in a
   * clinical audit trail.
   */
  'not_owner',
  /**
   * Authenticated, but this person belongs to no clinic at all (ADR-0027: the
   * clinic is the boundary, so there is nothing to scope a clinic route to).
   * The console's answer is the signup or invitation-claim screen, not an
   * apology — which is why `GET /api/clinics/mine` answers `{ clinic: null }`
   * instead of this code; this is for the routes that genuinely need one.
   */
  'no_clinic',
  /**
   * Authenticated and in a clinic, but not THIS clinic's — the caller has no
   * active membership of the clinic the addressed record sits in. Distinct
   * from `not_owner`, which is about one record inside a clinic both people
   * are in.
   */
  'not_member',
  /**
   * The caller's clinic exists but has not been approved by an operator yet
   * (ADR-0030 §3). Nothing is wrong and nothing was refused permanently: the
   * clinic is waiting. A pending clinic can see its own profile — this code
   * guards the routes that touch patients.
   */
  'clinic_pending',
  /**
   * Refused: this person already has an active membership of this clinic. A
   * second one would make "their roles" ambiguous, and roles are changed
   * through the change route, never by re-adding somebody.
   */
  'already_member',
  /**
   * Refused: this change would leave the clinic with no admin, and a clinic
   * with no admin can never add one again — nobody left can mint an
   * invitation or change a role. Hand admin over first, then step down.
   */
  'last_admin',
  /**
   * Invitation refused: the token resolves to nothing. 404 on `not_owner`'s
   * reasoning — tokens are unguessable, so an honest "no such invitation"
   * gives an attacker nothing and tells an honest person the truth.
   */
  'invite_invalid',
  /** Invitation refused: its window has passed. 410 — it existed and is gone. */
  'invite_expired',
  /**
   * Invitation refused: already claimed. An invitation admits one person; the
   * second needs their own. 409, a knowable state of the record.
   */
  'invite_claimed',
  /**
   * Grant refused: the role and its window do not go together — a `covering`
   * or `read-only` grant with no `validUntil` (ADR-0028 §2: cover is
   * time-boxed by construction), or a window that has already passed. 400,
   * because re-phrasing the request fixes it.
   */
  'grant_invalid',
  /**
   * Signup refused: the clinic terms were not agreed to, or were agreed to
   * under a version this build does not carry. The agreement IS the signup
   * (ADR-0029), so there is nothing to record without it.
   */
  'terms_required',
  /**
   * Refused: the addressed record belongs to a DIFFERENT clinic than the
   * caller's. Deliberately distinct from `not_member` (which is about the
   * caller's standing) and from `not_owner` (which is about one record inside
   * the caller's own clinic): this one says the boundary itself was crossed.
   */
  'wrong_clinic',
  /**
   * `/api/ops/*` refused: the signed-in clinician is not on the ops email
   * allow-list. Distinct from `not_owner`/`wrong_clinic` — this caller may
   * hold no clinic membership at all, and none is relevant here; the ops
   * surface checks the account itself, not any clinic's roster.
   */
  'not_operator',
  /**
   * The session board refused (APP-MONITORING §14, owner 2026-09-13): the
   * account is on the ops allow-list, but this sign-in has not passed two-step
   * verification (the token's `aal` claim is not `aal2`). Only the board's
   * routes ask for it; the `/ops` approvals page does not.
   */
  'mfa_required',
  /**
   * `POST /api/ops/clinics/:clinicId/approve` refused: a `clinic_approvals`
   * row already exists. Approval is one row and there is no un-approval
   * (migration 022) — a second attempt is told, by whom and that it stands,
   * rather than answered as if it had just succeeded again.
   */
  'clinic_already_approved',
  /** Physio route called with no bearer token (A1, ADR-0015). */
  'auth_required',
  /** Bearer token failed verification: bad signature, expired, wrong issuer/audience. */
  'auth_invalid',
  /**
   * Too many attempts from this address, too quickly (TM-06). Only the two
   * self-service routes carry a limiter — clinic signup and invitation
   * claiming (ADR-0030 §2–§3) — because they are the only ones a person can
   * reach with an account they made themselves a minute ago. NOTHING clinical
   * is ever rate-limited: throttling a live session's taps or its credential
   * mint would be a clinical failure wearing a security control's clothes.
   *
   * ⚠ Added at build time in increment-12 §2.P4 (plan rule 12): the phase's own
   * limiter test proved the alternative was a `500 internal_error`, because the
   * limiter signals by throwing and the server's error handler owns every
   * throw. A person who tries twice too fast must be told to wait, not told the
   * platform broke.
   */
  'rate_limited',
  /** Credential minting failed upstream (Agora, storage). */
  'upstream_failure',
  /**
   * No such route on this API — an unmatched path or a method the path does
   * not serve.
   *
   * Distinct from `link_not_found` and the other absences, which mean "the
   * route exists and the THING is not there". This one means the caller and
   * the server disagree about what this API offers: a stale client, a typo, a
   * base URL pointing somewhere unintended. Worth its own code because those
   * are diagnosed completely differently — and because a bare framework 404
   * carries no request id for the caller to quote (M0.3).
   */
  'route_not_found',
  /** Anything genuinely unexpected. */
  'internal_error',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiError {
  readonly ok: false;
  readonly code: ErrorCode;
  /** Operator-facing, safe to display verbatim. Never contains PHI or internals. */
  readonly message: string;
  /**
   * The API's request id for the request that failed (APP-MONITORING M0.3).
   * Stamped by the server on the way out — never by a caller — so a client can
   * show *"reference: …"* and attach it to a problem report, and an operator
   * can go from that reference to this request's log lines.
   *
   * Optional because the TYPE is shared with clients that construct error
   * shapes locally (a network failure the request never survived has no
   * server-side id to carry). Every error body the API itself sends has one.
   */
  readonly requestId?: string;
}

/** HTTP status per code. Kept beside the codes so routes cannot drift from it. */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  link_not_found: 404,
  session_ended: 410,
  link_expired: 410,
  session_not_live: 409,
  invalid_request: 400,
  not_found: 404,
  consent_required: 403,
  artifact_already_uploaded: 409,
  artifact_purged: 410,
  // Both export refusals are 409, on `record_not_confirmable`'s precedent:
  // the episode exists (not 404 — a fake absence is what the `not_owner`
  // note already argues against), the caller owns it (not 403), the request
  // is well-formed and no re-phrasing of it would help (not 400), and the
  // condition is a NAMED state of the record rather than an unexpected
  // failure (not 500 — a data condition must not page an on-call).
  export_no_study_code: 409,
  export_unknown_protocol: 409,
  export_incomplete_technical_unresolved: 409,
  record_not_confirmable: 409,
  record_already_confirmed: 409,
  tap_superseded: 409,
  not_assessment_day: 409,
  assessment_already_filed: 409,
  observation_already_recorded: 409,
  observation_missing: 409,
  flag_already_open: 409,
  flag_already_resolved: 409,
  not_owner: 403,
  // The clinic boundary (increment-12 §2.P1). The three 403s are the three
  // ways a caller can stand outside a clinic-scoped record — no clinic, not
  // this clinic's member, member of another clinic — and they stay separate
  // because the console says something different for each.
  no_clinic: 403,
  not_member: 403,
  clinic_pending: 403,
  already_member: 409,
  last_admin: 409,
  invite_invalid: 404,
  invite_expired: 410,
  invite_claimed: 409,
  grant_invalid: 400,
  terms_required: 400,
  wrong_clinic: 403,
  not_operator: 403,
  mfa_required: 403,
  clinic_already_approved: 409,
  auth_required: 401,
  auth_invalid: 401,
  rate_limited: 429,
  upstream_failure: 502,
  route_not_found: 404,
  internal_error: 500,
};

/**
 * Default operator-facing copy. Deliberately plain: no error numbers, no jargon,
 * and each one ends with the next action rather than a diagnosis.
 */
export const ERROR_MESSAGE: Readonly<Record<ErrorCode, string>> = {
  link_not_found: 'This link is not valid. Please check with your physiotherapist for a new one.',
  session_ended: 'This session has ended. Your physiotherapist can send a new link.',
  // P4's exact sentence (DISCREPANCIES-V2 §2, D9), then the next action.
  link_expired:
    'This session link has expired and is no longer valid. Please ask your physiotherapist for a new link.',
  session_not_live:
    'Your physiotherapist has not started the session yet. Please try again shortly.',
  invalid_request: 'Something went wrong with that request. Please try again.',
  not_found: 'We could not find that. Please try again.',
  consent_required:
    'Recording is not permitted for this session — no recording consent is on record.',
  artifact_already_uploaded:
    'This part of the recording is already safely stored. The original stays as recorded.',
  artifact_purged:
    'This recording has been deleted at the end of its retention period. It cannot be restored or replaced.',
  export_no_study_code:
    'This episode has no study code, so it cannot be exported. A study code is assigned at enrolment and cannot be created here — please contact support.',
  export_unknown_protocol:
    'This episode follows a protocol version this app does not carry, so it cannot be exported. Please update the app, and contact support if it is already up to date.',
  export_incomplete_technical_unresolved:
    'This episode has a session closed for technical reasons, and how that day should appear in an export is still being decided. Please contact support.',
  record_not_confirmable:
    'This session cannot be confirmed yet. End the session and make sure its protocol is set, then try again.',
  record_already_confirmed:
    'This session is already confirmed and sealed. The signed record stands as recorded.',
  tap_superseded:
    'The tap record changed while this form was open — an undo from the call synced late. The form has refreshed with the current numbers; please review and sign again.',
  not_assessment_day:
    'This day is not an assessment day of the pinned protocol. Check the calendar and try again.',
  assessment_already_filed:
    'A formal assessment is already filed for this timepoint. To change it, file a correction — the original stays as recorded.',
  observation_already_recorded:
    'Observations are already recorded for this session. To change them, record a correction — the original stays as recorded.',
  observation_missing:
    'The session observations are not recorded yet. Answer all six fields — a number or “not assessed today” — then sign off.',
  flag_already_open:
    'That flag is already open for this patient. Resolve the open one before raising it again.',
  flag_already_resolved:
    'This flag is already resolved. The resolution stays as recorded — if it has recurred, raise it again.',
  not_owner: 'This record belongs to another clinician, so it cannot be opened here.',
  no_clinic: 'Your account is not part of a clinic yet. Register a clinic or use an invitation.',
  not_member: 'You are not a member of this clinic. Ask a clinic admin to invite you.',
  clinic_pending:
    'This clinic is still being reviewed, so patient records are not available yet. We will email you when it is approved.',
  already_member:
    'That person is already in this clinic. Change their roles instead of adding them again.',
  last_admin:
    'This clinic needs at least one admin. Make someone else an admin first, then make this change.',
  invite_invalid: 'This invitation is not valid. Please ask the clinic for a new one.',
  invite_expired: 'This invitation has expired. Please ask the clinic for a new one.',
  invite_claimed: 'This invitation has already been used. Please ask the clinic for your own.',
  grant_invalid:
    'That cover could not be set up. Covering and read-only access need an end date in the future.',
  terms_required: 'Please read and agree to the clinic terms to continue.',
  wrong_clinic: 'This record belongs to another clinic, so it cannot be opened here.',
  not_operator: 'Your account is not authorized to use the admin console.',
  mfa_required: 'Please finish two-step verification to open the session board.',
  clinic_already_approved:
    'This clinic is already approved. Approval cannot be undone or repeated.',
  auth_required: 'Please sign in to continue.',
  auth_invalid: 'Your sign-in is no longer valid. Please sign in again.',
  rate_limited: 'Too many attempts from here just now. Please wait a little and try again.',
  upstream_failure: 'We could not connect you just now. Please try again in a moment.',
  // Deliberately not "page not found": the only humans who ever see this are
  // an operator reading a log or a tester with a stale build, and neither is
  // helped by web copy about pages.
  route_not_found: 'That address is not part of this service. Please check the app version.',
  internal_error: 'Something went wrong on our side. Please try again in a moment.',
};

export function apiError(code: ErrorCode, message: string = ERROR_MESSAGE[code]): ApiError {
  return { ok: false, code, message };
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { ok?: unknown }).ok === false &&
    ERROR_CODES.includes((value as { code?: ErrorCode }).code as ErrorCode)
  );
}
